import React from 'react'
import { get, identity, isNil, noop } from 'lodash-es'
import { ValidationIssue } from 'api'
import { Field, TextInput, Focusable } from '../inputs'
import { MenuContainer } from '../menus'
import { iconSet } from '../icons'
import { Listbox, ListboxOptions, OptionKey, OptionTemplateProps, ListboxProps } from './Listbox'
import { DropdownListbox, SelectboxPrimary, SelectboxInput, DropdownButton, SelectboxOptionTemplate } from './optionsStyles'

export interface ComboboxProps<T, K extends OptionKey> extends ListboxOptions<T, K> {
  id?: string
  className?: string
  title?: string | JSX.Element
  readOnly?: boolean
  disabled?: boolean
  withPortal?: boolean
  validationIssues?: ValidationIssue[]
  canClear?: boolean
  placeholder?: string
  placeholderReadOnly?: string
  secondaryDropdown?: React.ReactElement<ListboxProps<any, OptionKey>>
  value: K | null | undefined
  dataTestId?: string
  filterFields?: string[]
  onUpdate: (optionKey: K | null, ...rest: any[]) => void
  onPaste?: (evt: React.ClipboardEvent) => unknown
  inputDecorator?: (content: JSX.Element) => JSX.Element
  contentDecorator?: (content: JSX.Element) => JSX.Element
  onOptionSelected?: (optionKey: K) => void
  isVirtualized?: boolean
}

interface ComboboxState {
  isDroppedDown: boolean
  filterText: string | undefined
}

export class Combobox extends React.Component<ComboboxProps<any, OptionKey>, ComboboxState> implements Focusable {
  constructor(props: ComboboxProps<any, OptionKey>) {
    super(props)
    this.state = { isDroppedDown: false, filterText: undefined }
  }

  private listboxRef = React.createRef<Listbox>()
  private inputRef = React.createRef<HTMLInputElement>()

  public setFocus = () => {
    if (this.inputRef.current) {
      this.inputRef.current.select()
    }
  }

  private getSelectedOption(props: ComboboxProps<any, OptionKey>) {
    if (isNil(props.value)) {
      return null
    } else if (props.getOption) {
      return props.getOption(props.value)
    } else {
      return props.value
    }
  }

  public getInputValueText() {
    if (!isNil(this.state.filterText)) {
      return this.state.filterText
    } else {
      const selectedOption = this.getSelectedOption(this.props)

      if (!isNil(selectedOption)) {
        return this.props.getOptionText ? this.props.getOptionText(selectedOption) : selectedOption
      }

      return undefined
    }
  }

  public render() {
    const { readOnly } = this.props
    return readOnly ? this.renderReadOnly() : this.renderEditable()
  }

  private renderReadOnly() {
    const selectedOption = this.getSelectedOption(this.props)
    let text = this.props.placeholderReadOnly

    if (!isNil(selectedOption)) {
      text = this.props.getOptionText ? this.props.getOptionText(selectedOption) : selectedOption
    }

    return (
      <TextInput
        id={this.props.id}
        className={this.props.className}
        value={text}
        readOnly={this.props.readOnly}
        disabled={this.props.disabled}
        onUpdate={noop}
      />
    )
  }

  private renderOption = (optionProps: OptionTemplateProps<any, OptionKey>) => {
    return <SelectboxOptionTemplate {...optionProps}>{optionProps.optionText}</SelectboxOptionTemplate>
  }

  private renderEditable() {
    const {
      id,
      placeholder,
      className,
      canClear,
      disabled,
      validationIssues,
      secondaryDropdown,
      value,
      optionKeys,
      optionTemplate,
      dataTestId,
      contentDecorator = identity,
      inputDecorator,
      onOptionSelected,
      onPaste,
      isVirtualized,
      ...rest
    } = this.props
    const { isDroppedDown } = this.state
    const inputValueText = this.getInputValueText()
    const allOptionKeys = canClear ? [null, ...optionKeys] : optionKeys

    const component = (
      <SelectboxPrimary disabled={disabled} readOnly={false} validationIssues={validationIssues}>
        {(inputDecorator ?? identity)(
          <SelectboxInput
            data-test-id={dataTestId}
            id={id}
            placeholder={placeholder}
            type="text"
            autoComplete="off"
            spellCheck={false}
            disabled={disabled}
            value={inputValueText || ''}
            onFocus={this.handleFocus}
            onChange={this.handleFilterTextChange}
            onKeyDown={this.handleFilterTextKeyDown}
            onPaste={onPaste}
            ref={this.inputRef}
          />
        )}

        <DropdownButton
          onClick={this.setFocus}
          canFocus={inputDecorator ? true : false}
          disabled={disabled}
          readOnly={false}
          validationIssues={validationIssues}>
          {isDroppedDown ? iconSet.chevronUp : iconSet.chevronDown}
        </DropdownButton>
      </SelectboxPrimary>
    )

    return (
      <MenuContainer
        className={className}
        disabled={disabled}
        withPortal={this.props.withPortal}
        menuVisible={this.state.isDroppedDown}
        primaryComponent={component}
        onUpdateMenuVisible={this.handleUpdateMenuVisible}>
        {contentDecorator(
          <>
            {secondaryDropdown &&
              React.cloneElement(secondaryDropdown, {
                filterOption: this.handleSecondaryDropdownOptionFilter,
                optionTemplate: secondaryDropdown.props.optionTemplate || this.renderOption
              })}
            <DropdownListbox
              {...rest}
              value={value}
              ref={this.listboxRef}
              enableHighlight={true}
              optionKeys={allOptionKeys}
              optionTemplate={optionTemplate || this.renderOption}
              onUpdate={onOptionSelected || this.handleOptionSelected}
              filterOption={this.handleOptionFilter}
              isVirtualized={isVirtualized}
            />
          </>
        )}
      </MenuContainer>
    )
  }

  private handleOptionFilter = (optionKey: OptionKey, option: any, optionText: string) => {
    let result = true

    if (this.props.filterOption) {
      result = this.props.filterOption(optionKey, option, optionText)
    }

    if (result) {
      result = this.isOptionFilterTextMatch(optionKey, option, optionText)
    }

    return result
  }

  private handleSecondaryDropdownOptionFilter = (optionKey: OptionKey, option: any, optionText: string) => {
    const { secondaryDropdown } = this.props

    let result = true

    if (secondaryDropdown && secondaryDropdown.props.filterOption) {
      result = secondaryDropdown.props.filterOption(optionKey, option, optionText)
    }

    if (result) {
      result = this.isOptionFilterTextMatch(optionKey, option, optionText)
    }

    return result
  }

  private isOptionFilterTextMatch = (optionKey: OptionKey, option: object, optionText: string) => {
    const { filterFields = [] } = this.props
    const filterText = this.state.filterText || ''
    const optionKeyText = isNil(optionKey) ? '' : optionKey.toString()
    return (
      optionText.toLowerCase().startsWith(filterText.toLowerCase()) ||
      optionKeyText.toLowerCase().startsWith(filterText.toLowerCase()) ||
      !!filterFields.some(field =>
        get(option, field)
          ?.toLowerCase()
          .startsWith(filterText.toLowerCase())
      )
    )
  }

  private handleOptionSelected = (optionKey: OptionKey) => {
    this.setState({ filterText: undefined, isDroppedDown: false }, () => this.props.onUpdate(optionKey))
  }

  private handleFilterTextKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (this.state.isDroppedDown && this.listboxRef.current) {
      this.listboxRef.current.handleKeyDown(event)
    }
  }

  private handleUpdateMenuVisible = (menuVisible: boolean) => {
    this.setState({ isDroppedDown: menuVisible, filterText: menuVisible ? this.state.filterText : undefined })
  }

  private handleFilterTextChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    this.setState({ filterText: event.currentTarget.value })
  }

  private handleFocus = (_ev: React.FocusEvent<HTMLElement>) => {
    this.setFocus()
  }
}

export const ComboboxField = Field(Combobox)
